import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { Language } from "../../App";
import { toast } from "react-toastify";
import { apiAdmin, getApiErrorMessage } from "../../services/api";
import { CHAT, NEW_PROFILE_DOCTOR } from "../../constants/RoutesUrl";
import { IoClose, IoCalendarOutline, IoTimeOutline, IoChatbubbleOutline, IoOpenOutline, IoVideocamOutline } from "react-icons/io5";
import { PiFilePdf } from "react-icons/pi";
import { FaChevronDown } from "react-icons/fa";
import moment from "moment";
import ModernDatePicker from "../common/ModernDatePicker";
import ModernTimePicker from "../common/ModernTimePicker";
import { MdOutlineSms } from "react-icons/md";
import zoom from "../../assets/images/zoom.svg";
import { LiaVideoSolid } from "react-icons/lia";
import { _imagesURL } from "../../constants/API";
import placeholderAvatar from "../../assets/images/icon/placeholder_Awatar.png";

// A function (not a static array) so the "minutes" word stays in sync with
// the same word used to prefill `duration` from mentee.durationMinutes below
// — both need to build the exact same string for FieldSelect to show the
// right option as selected.
function getDurationOptions(minutesWord) {
  return [15, 30, 45, 60, 90].map((n) => `${n} ${minutesWord}`);
}

// The handful of zones doctors on this platform actually need — covering
// Europe (this app's home market), US east/west, and Karachi/Dubai (where
// most of the current mentor/mentee base is) — instead of every IANA zone.
const TIMEZONES = ["UTC", "Europe/Madrid", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Karachi", "Asia/Dubai"];

// "Asia/Karachi" -> "Asia/Karachi" — just the zone name, no GMT/UTC offset.
function formatTimezoneLabel(tz) {
  return tz.replace(/_/g, " ");
}

const TIMEZONE_OPTIONS = TIMEZONES.map(formatTimezoneLabel);

function CheckSvg() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
      <path
        d="M1 4L3.5 6.5L9 1"
        stroke="#00BADA"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// A native <select>'s open direction (up vs down) is decided by the
// browser itself based on available viewport space — with ~400 timezone
// options that list almost never fits below, so it was opening upward and
// covering the field. This custom dropdown is positioned explicitly below
// its trigger and scrolls internally instead, so it always opens down.
function FieldSelect({ value, onChange, options, placeholder, error, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div>
      <div className="position-relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          disabled={disabled}
          className="d-flex align-items-center justify-content-between w-100"
          style={{
            border: `1px solid ${error ? "#E0263F" : "#E6E7EA"}`,
            borderRadius: "10px",
            padding: "10px 12px",
            fontSize: "13px",
            color: value ? "#0E1421" : "#8F98A3",
            background: disabled ? "#F5F6F8" : "#fff",
            textAlign: "left",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span className="text-truncate" style={{ minWidth: 0 }}>{value || placeholder}</span>
          <FaChevronDown
            size={10}
            color="#5F6673"
            style={{
              flexShrink: 0,
              marginLeft: "8px",
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        </button>

        {isOpen && (
          <div
            className="position-absolute"
            style={{
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 20,
              maxHeight: "220px",
              overflowY: "auto",
              background: "#fff",
              border: "1px solid #E6E7EA",
              borderRadius: "10px",
              boxShadow: "0 8px 24px rgba(15,26,43,0.16)",
            }}
          >
            {options.map((opt) => (
              <div
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
                className="text-truncate"
                style={{
                  padding: "8px 12px",
                  fontSize: "13px",
                  cursor: "pointer",
                  color: opt === value ? "#00BADA" : "#0E1421",
                  background: opt === value ? "#EAFBFE" : "transparent",
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && (
        <p className="fs-11 mb-0 mt-1" style={{ color: "#E0263F" }}>
          {error}
        </p>
      )}
    </div>
  );
}

function mentorshipSubtitle(mentee, lang) {
  if (!mentee) return lang?.mentorship_default_subtitle || "Mentorship Session";
  const caseReview = lang?.mentorship_case_review || "Case Review Mentorship";
  if (mentee.specialty) return `${mentee.specialty} ${caseReview}`;
  return caseReview;
}

// Reconstructs the actual saved session time from the raw fields
// MentorshipRequests.jsx's mapActivity passes through (startTimestamp /
// startDate+startTime) — used to pre-fill the reschedule view with what was
// really booked, instead of re-parsing the formatted `meta` display string.
function getMenteeSessionMoment(mentee) {
  if (mentee?.startTimestamp) {
    const m = moment.unix(Number(mentee.startTimestamp));
    if (m.isValid()) return m;
  }
  if (mentee?.startDate && mentee?.startTime) {
    const m = moment(
      `${mentee.startDate} ${mentee.startTime}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD h:mm A"]
    );
    if (m.isValid()) return m;
  }
  return null;
}

// Only the parts validate()/handleConfirm actually use for each field —
// date compares by calendar day (its H/m carried over from the parsed
// moment are irrelevant), startTime by hour+minute (its Y/M/D are
// irrelevant) — so picking the same day/time again after nudging the
// picker and back doesn't read as "changed".
function isSameCalendarDate(a, b) {
  const aValid = a?.isValid?.();
  const bValid = b?.isValid?.();
  if (!aValid || !bValid) return aValid === bValid;
  return a.isSame(b, "day");
}
function isSameClockTime(a, b) {
  const aValid = a?.isValid?.();
  const bValid = b?.isValid?.();
  if (!aValid || !bValid) return aValid === bValid;
  return a.hour() === b.hour() && a.minute() === b.minute();
}

function MentorShipSchedule({ isOpen, toggle, mentee }) {
  const navigate = useNavigate();
  const { lang } = useContext(Language);
  const queryClient = useQueryClient();
  const durationOptions = getDurationOptions(lang?.minutes || "minutes");
  const [date, setDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [duration, setDuration] = useState("");
  const [timezone, setTimezone] = useState("");
  const [notifyViaChat, setNotifyViaChat] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Snapshot of what a "Sessions Confirmed" item was actually prefilled
  // with, taken once when the modal opens — Reschedule Session stays
  // disabled until at least one field diverges from this, so hitting the
  // API is only ever a real reschedule, not a no-op resubmit of the same
  // date/time/duration/timezone.
  const initialScheduleRef = useRef(null);

  const isConfirmed = mentee?.status === "Sessions Confirmed";
  // A live session — same prefilled/read-only shape as a confirmed one
  // (there's a real booked time and Zoom link to show), but with no
  // reschedule action: the call is already happening, there's nothing left
  // to confirm or send to the mentee.
  const isInProgress = mentee?.status === "Mentorship in Progress";

  // Check if session duration has ended
  const sessionMoment = getMenteeSessionMoment(mentee);
  const durationMins = Number(mentee?.durationMinutes) || 0;
  const endSeconds =
    sessionMoment && durationMins ? sessionMoment.unix() + durationMins * 60 : null;
  const nowSeconds = Math.floor(Date.now() / 1000);
  const isDurationCompleted =
    !mentee?.isCompleted &&
    (isConfirmed || isInProgress) &&
    endSeconds !== null &&
    nowSeconds >= endSeconds;

  // Resets to blank for a fresh "Awaiting Mentor Response" item, or
  // pre-fills with the already-booked time for a "Sessions Confirmed" /
  // "Mentorship in Progress" one — runs whenever the modal opens for a
  // (possibly different) mentee, so fields never carry over stale values
  // from the last person scheduled.
  useEffect(() => {
    if (!isOpen || !mentee) return;
    if (isConfirmed || isInProgress) {
      const parsed = getMenteeSessionMoment(mentee);
      const durationStr = mentee.durationMinutes ? `${mentee.durationMinutes} ${lang?.minutes || "minutes"}` : "";
      const timezoneStr = mentee.timezone ? formatTimezoneLabel(mentee.timezone) : "";
      setDate(parsed);
      setStartTime(parsed);
      setDuration(durationStr);
      setTimezone(timezoneStr);
      initialScheduleRef.current = isConfirmed
        ? { date: parsed, startTime: parsed, duration: durationStr, timezone: timezoneStr }
        : null;
    } else {
      setDate(null);
      setStartTime(null);
      setDuration("");
      setTimezone("");
      initialScheduleRef.current = null;
    }
    setNotifyViaChat(false);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mentee]);

  // Same curtain-modal pattern as ApplyMentorShip.jsx / NewCourseDetail2.0.jsx's
  // VideoModal — plain backdrop + content divs animated with @keyframes,
  // not reactstrap's Modal/Fade, so open and close are both immediate and
  // symmetric instead of gated behind a JS timeout.
  const [isClosing, setIsClosing] = useState(false);
  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!mentee) return null;
  if (!isOpen && !isClosing) return null;

  const handleGoToProfile = () => {
    if (!mentee.profileId) return;
    closeThen(() => navigate(`${NEW_PROFILE_DOCTOR}/${mentee.profileId}`));
  };

 
  const handleGoToChat = () => {
    if (!mentee.profileId) return;
    const loginId = JSON.parse(localStorage.getItem("SplashData") || "null")?.user?.id;
    closeThen(() =>
      navigate(CHAT, {
        state: {
          from: loginId,
          to: mentee.profileId,
          group_id: 0,
          user: {
            user_id: mentee.profileId,
            name: mentee.name,
            image: "",
          },
        },
      })
    );
  };

  
  const validate = () => {
    const newErrors = {};
    if (!date || !date.isValid?.()) newErrors.date = lang?.mentorship_error_date || "Please select the date.";
    if (!startTime || !startTime.isValid?.()) newErrors.startTime = lang?.mentorship_error_time || "Please select the time.";

    // Catches a past date on its own (independent of startTime) — the
    // picker's own minDate already blocks picking one, but this is the
    // fallback if a past date ever ends up in state some other way, and it
    // points the error at the date field instead of only surfacing once
    // startTime also happens to be set.
    if (date?.isValid?.() && date.isBefore(moment(), "day")) {
      newErrors.date =
        lang?.mentorship_error_past_date ||
        "Please select today's date, then choose a future date.";
    }

    if (date?.isValid?.() && startTime?.isValid?.()) {
      const combined = moment(date).set({
        hour: startTime.hour(),
        minute: startTime.minute(),
        second: 0,
        millisecond: 0,
      });
      const isToday = date.isSame(moment(), "day");
      const minSelectable = isToday ? moment().add(10, "minutes") : moment();
      if (combined.isBefore(minSelectable)) {
        newErrors.startTime = isToday
          ? lang?.mentorship_error_min_10_minutes || "For today's date, start time must be at least 10 minutes from current time."
          : lang?.mentorship_error_past_time || "Please select a future time.";
      }
    }
    if (!duration) newErrors.duration = lang?.mentorship_error_duration_select || "Please select the duration.";
    if (!timezone) newErrors.timezone = lang?.mentorship_error_timezone || "Please select the timezone.";
    // Only asked for on a fresh request — reschedule has no checkbox to check.
    if (!isConfirmed && !notifyViaChat) {
      newErrors.notifyViaChat = lang?.mentorship_error_notify_checkbox || "Please check this box to continue.";
    }
    return newErrors;
  };

  
  const isRescheduleUnchanged =
    isConfirmed &&
    !!initialScheduleRef.current &&
    isSameCalendarDate(date, initialScheduleRef.current.date) &&
    isSameClockTime(startTime, initialScheduleRef.current.startTime) &&
    duration === initialScheduleRef.current.duration &&
    timezone === initialScheduleRef.current.timezone;

  const handleConfirm = async () => {
    // Belt-and-braces alongside the button's own `disabled` below — nothing
    // to send to the API when reschedule fields are still exactly what was
    // already booked.
    if (isRescheduleUnchanged) return;
    const newErrors = validate();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const startDateStr = date.format("YYYY-MM-DD");
      const startTimeStr = startTime.format("HH:mm:ss");
      const startTimestamp = moment(
        `${startDateStr} ${startTimeStr}`,
        "YYYY-MM-DD HH:mm:ss"
      ).unix();
      // "90 minutes" -> 90 (activity.duration elsewhere in the API is a
      // plain minute count, not this dropdown's display label).
      const durationMinutes = parseInt(duration, 10) || 0;

      // apiAdmin's interceptor (src/services/api.js) already attaches this
      // same Bearer token to every request — set explicitly here too so the
      // header is visibly present on this specific call, matching how
      // getSplashData() does it in that same file.
      const token = JSON.parse(localStorage.getItem("SignupData") || "null")?.data?.token;
      const res = await apiAdmin.post(
        "user/mentor/schedule",
        {
          request_id: mentee.id,
          start_date: startDateStr,
          start_time: startTimeStr,
          start_timestamp: startTimestamp,
          duration: durationMinutes,
          timezone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.status) {
        const template = isConfirmed
          ? lang?.mentorship_rescheduled_toast || "Session with {{name}} rescheduled to {{date}} at {{time}}."
          : lang?.mentorship_scheduled_toast || "Session scheduled with {{name}} on {{date}} at {{time}}.";
        toast.success(
          template
            .replace("{{name}}", mentee.name)
            .replace("{{date}}", date.format("DD MMM YYYY"))
            .replace("{{time}}", startTime.format("hh:mm A"))
        );
        // Refetch MentorshipRequests' list so this row picks up its new
        // status instead of still showing "Awaiting" until a manual refresh.
        queryClient.invalidateQueries(["mentorshipActivity"]);
        closeThen();
      } else {
        toast.error(getApiErrorMessage(res.data, lang?.mentorship_error_schedule || "Couldn't schedule this session. Please try again."));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data, lang?.mentorship_error_schedule || "Couldn't schedule this session. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!mentee?.id) return;
    setCompleting(true);
    try {
      const token = JSON.parse(localStorage.getItem("SignupData") || "null")?.data?.token;
      const res = await apiAdmin.post(
        "user/mentor/complete",
        { request_id: mentee.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.status) {
        toast.success(
          lang?.mentorship_session_completed_toast || "Mentorship session completed successfully."
        );
        queryClient.invalidateQueries(["mentorshipActivity"]);
        closeThen();
      } else {
        toast.error(
          getApiErrorMessage(
            res.data,
            lang?.mentorship_error_complete || "Couldn't complete this session. Please try again."
          )
        );
      }
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err.response?.data,
          lang?.mentorship_error_complete || "Couldn't complete this session. Please try again."
        )
      );
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      className={`mss-curtain-backdrop ${isClosing ? "closing" : ""}`}
      onClick={handleClose}
    >
      <style>{`
        /* Same curtain-modal pattern as ApplyMentorShip.jsx — plain
           backdrop + content divs animated with @keyframes, driven by our
           own isClosing state instead of reactstrap's Modal/Fade, so open
           and close are both immediate and symmetric. */
        .mss-curtain-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
          animation: mssBackdropFadeIn 0.8s ease-out forwards;
        }
        .mss-curtain-backdrop.closing {
          animation: mssBackdropFadeOut 0.8s ease-out forwards;
        }
        .mss-curtain-content {
          position: relative;
          width: 100%;
          max-width: 539px;
          margin: auto;
          background: #fff;
          /* Not "overflow: hidden"/auto here — the timezone dropdown is an
             absolutely positioned list that needs to render past this
             content's own edge without being clipped by it. The backdrop
             above scrolls instead if the content is ever taller than the
             viewport. */
          border: 0.5px solid #E6E7EA;
          border-radius: 26px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15);
          transform: translateY(-100vh);
          animation: mssCurtainOpen 0.8s ease-out forwards;
        }
        .mss-curtain-content.closing {
          animation: mssCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes mssBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes mssBackdropFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes mssCurtainOpen {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }
        @keyframes mssCurtainClose {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .mss-curtain-backdrop, .mss-curtain-content {
            animation: none !important;
          }
        }
      `}</style>
      <div
        className={`mss-curtain-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff" }}
      >
      <div className="position-relative" style={{ padding: "28px" }}>
        {/* Close */}
        <button
          type="button"
          onClick={handleClose}
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{
            top: "16px",
            right: "16px",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #F7F7F7",
            boxShadow: "0 2px 8px rgba(28, 37, 51, 0.16)",
          }}
        >
          <IoClose size={18} color="#5F6673" />
        </button>

        {/* Header */}
        <h5 className=" fs-18 mb-1" style={{ color: "#031316", fontWeight: "700" }}>
          {isDurationCompleted
            ? lang?.mentorship_session_completed_title || "Session Duration Completed"
            : isInProgress
            ? lang?.mentorship_session_in_progress_title || "Session in Progress"
            : isConfirmed
            ? lang?.mentorship_scheduled_session_title || "Scheduled Session"
            : lang?.mentorship_schedule_session_title || "Schedule your session"}
        </h5>
        <p className="fs-12 mb-4" style={{ color: "#5F6673", fontWeight: "500" }}>
          {isDurationCompleted
            ? lang?.mentorship_session_completed_subtitle || "This session's duration has ended. Please mark it as complete."
            : isInProgress
            ? lang?.mentorship_session_live_subtitle || "This mentorship session is currently live — join it below."
            : isConfirmed
            ? lang?.mentorship_session_already_scheduled_subtitle || "Session already scheduled — update the date or time if needed."
            : lang?.mentorship_pick_date_time_subtitle || "Pick a date and time — the Zoom link will be generated automatically."}
        </p>

        {/* MENTEE ACCOUNT DETAILS */}
        <span
          className="fs-12 d-block mb-2"
          style={{ color: "#031316", fontWeight: "500", letterSpacing: "0.1em" }}
        >
          {lang?.mentorship_mentee_account_details_label || "MENTEE ACCOUNT DETAILS"}
        </span>
        <div
          className="d-flex align-items-center justify-content-between mb-3"
          style={{ border: "1px solid #D7E0EA", borderRadius: "20px", padding: "10px 14px" }}
        >
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <img
              src={
                mentee.image
                  ? (mentee.image.startsWith("http") || mentee.image.startsWith("data:")
                      ? mentee.image
                      : `${_imagesURL}${mentee.image.startsWith("/") ? "" : "/"}${mentee.image}`)
                  : placeholderAvatar
              }
              alt={mentee.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = placeholderAvatar;
              }}
              style={{ width: "42px", height: "42px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <span className="fs-14 d-block text-truncate" style={{ color: "#0E1421", fontWeight: "500" }}>
                {mentee.name}
              </span>
              <span className="fs-12  d-block text-truncate" style={{ color: "#5F6673", fontWeight: "400" }}>
                {mentorshipSubtitle(mentee, lang)}
              </span>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleGoToChat}
              className="position-relative d-flex align-items-center justify-content-center"
              style={{ padding: "5px", width: "48px", borderRadius: "20px", border: "1px solid #D7E0EA", background: "#fff" }}
            >
              <MdOutlineSms size={16} color="#6D7683" />
              {mentee.statusCount ? (
                <span
                  className="position-absolute d-flex align-items-center justify-content-center text-white"
                  style={{
                    top: "-6px",
                    right: "-6px",
                    height: "16px",
                    // Same explicit width-tier check as MentorshipRequests.jsx's
                    // badge — 1 digit stays a 16px circle, 2+/3+ digits step
                    // up to fixed pill widths.
                    width:
                      mentee.statusCount >= 100
                        ? "38px"
                        : mentee.statusCount >= 10
                          ? "24px"
                          : "16px",
                    borderRadius: "999px",
                    boxSizing: "border-box",
                    lineHeight: "16px",
                    border:"1px solid #00000026",
                    background: "#e53935",
                    fontSize: "12px",
                  }}
                >
                  {mentee.statusCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={handleGoToProfile}
              className=" text-white roboto-semibold fs-13"
              style={{ background: "#00BADA", borderRadius: "20px", border: "1px solid #D7E0EA", padding: "6px 16px", whiteSpace: "nowrap" }}
            >
              {lang?.mentorship_go_to_profile_button || "Go to Profile"}
            </button>
          </div>
        </div>

        {/* CV — only shown when this request actually has one attached
            (activity.data from the API), not a settings-based guess */}
        {mentee.cvUrl && (
          <div
            className="d-flex align-items-center justify-content-between mb-3"
            style={{ border: "1px solid #D7E0EA", borderRadius: "20px", padding: "10px 14px" }}
          >
            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
              <span
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: "34px", height: "34px", borderRadius: "10px", background: "#15803D1A", border: "1px solid #15803D4D", color: "#15803D" }}
              >
                <PiFilePdf size={18} />
              </span>
              <div style={{ minWidth: 0 }}>
                <span className="fs-14 roboto-bold d-block text-truncate" style={{ color: "#0E1421", fontWeight: "500" }}>
                  {mentee.cvFileName}
                </span>
                <span className="fs-12 d-block" style={{ color: "#5F6673", fontWeight: "400" }}>
                  {lang?.mentorship_pdf_format || "PDF format"}
                </span>
              </div>
            </div>
            <a
              href={mentee.cvUrl}
              target="_blank"
              rel="noreferrer"
              className="d-flex align-items-center gap-1 bg-transparent  fs-13 flex-shrink-0 text-decoration-none"
              style={{ color: "#6D7683", borderRadius: "20px", padding: "4px", border: "1px solid #E6E7EA", fontWeight: "500" }}
            >
              <IoOpenOutline size={13} /> {lang?.mentorship_open_link || "Open"}
            </a>
          </div>
        )}

        {/* SCHEDULE SESSION */}
        <span
          className="fs-12 d-block mb-2"
          style={{ color: "#031316", fontWeight: "500", letterSpacing: "0.1em" }}
        >
          {lang?.mentorship_schedule_session_label || "SCHEDULE SESSION"}
        </span>

          <div className="row gx-3 gy-3 mb-3">
            <div className="col-6">
              <span className="fs-13 d-block mb-1" style={{ color: "#0E1421", fontWeight: "500" }}>
                {lang?.date || "Date"}
              </span>
              <ModernDatePicker
                value={date}
                minDate={moment().startOf("day")}
                disabled={isDurationCompleted}
                onChange={(newValue) => {
                  setDate(newValue);
                  setErrors((prev) => ({ ...prev, date: "" }));
                }}
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                error={Boolean(errors.date)}
              />
              {errors.date && (
                <p className="fs-11 mb-0 mt-1" style={{ color: "#E0263F" }}>
                  {errors.date}
                </p>
              )}
            </div>
            <div className="col-6">
              <span className="fs-13 d-block mb-1" style={{ color: "#0E1421", fontWeight: "500" }}>
                {lang?.start_time || "Start Time"}
              </span>
              <ModernTimePicker
                value={startTime}
                disabled={isDurationCompleted}
                onChange={(newValue) => {
                  setStartTime(newValue);
                  setErrors((prev) => ({ ...prev, startTime: "" }));
                }}
                placeholder={lang?.mentorship_select_time_placeholder || "Select your time"}
                error={Boolean(errors.startTime)}
                relatedDate={date}
                minTime={
                  date && date.isSame(moment(), "day")
                    ? moment().add(10, "minutes")
                    : undefined
                }
              />
              {errors.startTime && (
                <p className="fs-11 mb-0 mt-1" style={{ color: "#E0263F" }}>
                  {errors.startTime}
                </p>
              )}
            </div>
          </div>

        <div className="row gx-3 gy-3 mb-3">
          <div className="col-6">
            <span className="fs-13  d-block mb-1" style={{ color: "#0E1421", fontWeight: "500" }}>
              {lang?.mentorship_duration_label || "Duration"}
            </span>
            <FieldSelect
              value={duration}
              onChange={(value) => {
                setDuration(value);
                setErrors((prev) => ({ ...prev, duration: "" }));
              }}
              options={durationOptions}
              placeholder={lang?.mentorship_select_duration_placeholder || "Select duration"}
              error={errors.duration}
              disabled={isDurationCompleted}
            />
          </div>
          <div className="col-6">
            <span className="fs-13  d-block mb-1" style={{ color: "#0E1421", fontWeight: "500" }}>
              {lang?.mentorship_timezone_label || "Timezone"}
            </span>
            <FieldSelect
              value={timezone}
              onChange={(value) => {
                setTimezone(value);
                setErrors((prev) => ({ ...prev, timezone: "" }));
              }}
              options={TIMEZONE_OPTIONS}
              placeholder={lang?.mentorship_select_timezone_placeholder || "Select timezone"}
              error={errors.timezone}
              disabled={isDurationCompleted}
            />
          </div>
        </div>

        {/* Notify via chat — a fresh request only, nothing to notify about
            when just updating an already-confirmed session or viewing a
            live one */}
        {!isConfirmed && !isInProgress && (
          <div style={{ borderTop:"1px solid #DCE5EE",paddingTop:"10px"}} className="mb-3">
            <div
              className="d-flex align-items-start gap-2"
              onClick={() => {
                setNotifyViaChat((prev) => !prev);
                setErrors((prev) => ({ ...prev, notifyViaChat: "" }));
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: "18px",
                  height: "18px",
                  marginTop: "1px",
                  borderRadius: "4px",
                  border: `1.5px solid ${errors.notifyViaChat ? "#E0263F" : notifyViaChat ? "#1DC1DD" : "#00BADA"}`,
                  background: notifyViaChat ? "#1DC1DD33" : "#fff",
                  cursor: "pointer",
                }}
              >
                {notifyViaChat && <CheckSvg />}
              </div>
              <p
                className="fs-12 mb-0"
                style={{ color: "#5F6673", fontWeight: "500", marginTop: "2px", lineHeight: "18px", cursor: "pointer" }}
              >
                {lang?.mentorship_notify_via_chat_desc || "Send this Zoom link and session details to the mentee automatically via chat."}
              </p>
            </div>
            {errors.notifyViaChat && (
              <p className="fs-11 mb-0 mt-1" style={{ color: "#E0263F" }}>
                {errors.notifyViaChat}
              </p>
            )}
          </div>
        )}

        {/* Zoom link — once a session is confirmed or already live (hidden when duration completed) */}
        {(isConfirmed || isInProgress) && !isDurationCompleted && (
          <div
            className="d-flex align-items-center justify-content-between mb-3"
            style={{ border: "1px solid #D7E0EA", borderRadius: "16px", padding: "6px 14px", }}
          >
            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
             <img src={zoom} alt="" />
              <div style={{ minWidth: 0 }}>
                <span className="fs-13 d-block text-truncate" style={{ color: "#0E1421", fontWeight: "600" }}>
                  {isInProgress
                    ? lang?.mentorship_session_live_label || "Session is live"
                    : lang?.mentorship_zoom_ready_label || "Zoom session ready"}
                </span>
                <span className="fs-11 d-block" style={{ color: "#5F6673", fontWeight: "400" }}>
                  {lang?.mentorship_join_via_zoom_desc || "Join the session via Zoom"}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                // mentee.meetingUrl already picked whichever of the API's
                // two link fields (meeting_url / meeting_host_url) actually
                // came back populated — see mapActivity() in
                // MentorshipRequests.jsx. Opened in a new tab so the doctor
                // doesn't lose this modal/page.
                if (!mentee.meetingUrl) {
                  toast.error(lang?.mentorship_zoom_link_unavailable || "The Zoom link isn't available yet.");
                  return;
                }
                window.open(mentee.meetingUrl, "_blank", "noopener,noreferrer");
              }}
              className="d-flex align-items-center gap-1 border-0 text-white  fs-12 flex-shrink-0"
              style={{ background: "#1DC1DD", borderRadius: "32px", fontWeight:"400", padding: "6px 10px" }}
            >
              <LiaVideoSolid size={16} /> {lang?.mentorship_join_button || "Join"}
            </button>
          </div>
        )}

        {/* Complete Session button (when duration completed) or Confirm/Reschedule */}
        {isDurationCompleted ? (
          <div className="d-flex justify-content-end">
            <button
              type="button"
              onClick={handleCompleteSession}
              disabled={completing}
              className="d-flex align-items-center justify-content-center gap-2 border-0 text-white"
              style={{
                background: "#1DC1DD",
                borderRadius: "999px",
                padding: "8px 28px",
                fontSize: "14px",
                fontWeight: "600",
                opacity: completing ? 0.7 : 1,
              }}
            >
              {completing
                ? lang?.mentorship_completing_button || "Completing…"
                : lang?.mentorship_complete_session_button || "Complete Session"}
            </button>
          </div>
        ) : (
          !isInProgress && (
            <div className="d-flex justify-content-end">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting || isRescheduleUnchanged}
                className="d-flex align-items-center justify-content-center gap-2 border-0 text-white "
                style={{
                  background: "#1DC1DD",
                  borderRadius: "999px",
                  padding: "8px 28px",
                  fontSize: "14px",
                  fontWeight: "700",
                  opacity: submitting || isRescheduleUnchanged ? 0.7 : 1,
                }}
              >
                {submitting
                  ? lang?.mentorship_scheduling_button || "Scheduling…"
                  : isConfirmed
                  ? lang?.mentorship_reschedule_button || "Reschedule Session"
                  : lang?.mentorship_confirm_send_button || "Confirm & Send to Mentee"}
              </button>
            </div>
          )
        )}
      </div>
      </div>
    </div>
  );
}

export default React.memo(MentorShipSchedule);